import React, {useEffect, useMemo} from 'react';

import {
    endOfMonth,
    endOfYear,
    getMonth,
    getYear,
    isSameMonth,
    isSameYear,
    startOfMonth,
    startOfYear,
} from 'date-fns';
import {useUnit} from 'effector-react';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';

import {MONTH_KEYS} from '@consts/date';
import {dateModel} from '@entities/date/model';
import {Select, SelectOption} from '@shared/ui/Select';
import {yearsOptions} from '@utils/select';

const getCurrentMonthRange = () => {
    const today = new Date();
    return [startOfMonth(today), endOfMonth(today)] as [Date, Date];
};

const isMonthSelected = (from: Date | null, to: Date | null) =>
    Boolean(from && to && isSameYear(from, to) && isSameMonth(from, to));

interface PeriodProps {
    requireMonth?: boolean;
}

export const Period = ({requireMonth = true}: PeriodProps) => {
    const {t} = useTranslation();
    const date = useUnit(dateModel);
    const {from, to} = date.state.range;
    const monthSelected = isMonthSelected(from, to);
    const selectedYear = getYear(from ?? new Date());

    useEffect(() => {
        if (!requireMonth || monthSelected) return;

        date.changed(getCurrentMonthRange());
    }, [date, monthSelected, requireMonth]);

    const monthOptions = useMemo<SelectOption<number>[]>(
        () =>
            MONTH_KEYS.map((key, index) => ({
                label: t(`dictionaries.months.${key}.short`),
                value: index,
            })),
        [t],
    );

    const yearOptions = useMemo<SelectOption<number>[]>(yearsOptions, []);
    const selectedMonth = monthSelected ? getMonth(from as Date) : null;

    const changeYear = (option: SelectOption<number> | null) => {
        const year = option?.value ?? getYear(new Date());
        const month = monthSelected ? getMonth(from as Date) : null;

        if (month == null) {
            const next = new Date(year, 0, 1);
            date.changed([startOfYear(next), endOfYear(next)]);
            return;
        }

        const next = new Date(year, month, 1);
        date.changed([startOfMonth(next), endOfMonth(next)]);
    };

    const changeMonth = (option: SelectOption<number> | null) => {
        const month = option?.value ?? null;

        if (month == null) {
            const next = new Date(selectedYear, 0, 1);
            date.changed([startOfYear(next), endOfYear(next)]);
            return;
        }

        const next = new Date(selectedYear, month, 1);
        date.changed([startOfMonth(next), endOfMonth(next)]);
    };

    return (
        <Wrapper>
            <Select
                size="sm"
                options={monthOptions}
                value={monthOptions.find((option) => option.value === selectedMonth) ?? null}
                onChange={(option: SelectOption<number> | null) => changeMonth(option)}
                placeholder={t('month')}
                isClearable={!requireMonth}
                openMenuOnFocus={false}
            />
            <Select
                size="sm"
                options={yearOptions}
                value={yearOptions.find((option) => option.value === selectedYear) ?? null}
                onChange={(option: SelectOption<number> | null) => changeYear(option)}
                placeholder={t('year')}
            />
        </Wrapper>
    );
};

const Wrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    width: 400px;

    & > * {
        flex: 1 1 0;
        min-width: 0;
    }
`;
